import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Form, Layout, PageHeader, Space } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { SizeForm } from "./Form";

const measureSizeGroupsQuery = gql`
  query ($pagination: ConnectionPagination, $searchQuery: String) {
    measureSizeGroups(pagination: $pagination, searchQuery: $searchQuery) {
      nodes {
        id
        code
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const createMeasureSizeMutation = gql`
  mutation ($input: CreteMeasureSizeInput!) {
    createMeasureSize(input: $input) {
      size {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const {
    error,
    loading: queryLoading,
    data,
  } = useQuery(measureSizeGroupsQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(createMeasureSizeMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const submit = (input: any) => {
    execMutation({ variables: { input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="..">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  return (
    <Fragment>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="New Size"
          extra={
            <Space size="large">
              <Button onClick={() => navigate("..")}>Cancel</Button>
              <Button type="primary" loading={loading} onClick={() => form.submit()}>
                Save
              </Button>
            </Space>
          }
        />
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}
            <SizeForm form={form} data={data} submit={submit} />
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
